import React, { ReactNode, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import styled from "styled-components";
import { AppMode } from "@lifeat/shared/system/AppModeTypes";
import { ProxiedRoutes } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import GlobeIcon from "@icons/GlobeIcon.svg";
import HeadphonesIcon from "@icons/HeadphonesIcon.svg";
import PlanningIcon from "@icons/PlanningIcon.svg";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { updateLastViewedAppMode } from "@user/service/UserPreferencesService";
import { BrowserUtil } from "@util/index";
import useDoesUserHavePlannerAccess from "../../../../features/task-management/planner/useDoesUserHavePlannerAccess";
import { SelectExploreViewEvent, SelectFocusViewEvent, SelectPlanningViewEvent } from "../../../AmplitudeAppModeEvents";
import { useAppModeContext } from "../../AppModeContext";

const StyledHeadphonesIcon = styled(HeadphonesIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledPlannerIcon = styled(PlanningIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledGlobeIcon = styled(GlobeIcon)`
    fill: ${({ theme }) => theme.iconColor};
`;

const StyledDropdownMenuContent = styled(DropdownMenuComponents.Content)`
    min-width: unset;
`;

const FOCUS_BUTTON_LABEL = "Focus";
const PLANNER_BUTTON_LABEL = "Plan";
const EXPLORE_BUTTON_LABEL = "Explore";
interface AppModeDropdownProps {}
export default function AppModeDropdown(props: AppModeDropdownProps) {
    const { appMode, setAppMode } = useAppModeContext();
    const amplitudeService = AmplitudeService.getInstance();
    const { user, userData } = useUserContext();
    const { setUpgradeCtaContext } = useCallToActionContext();

    // Business users will be prompted to do an onboarding with us first.
    const userHasPlannerAccess = useDoesUserHavePlannerAccess();
    const appModeOrDefault = appMode || "focus";
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleFocusClick = () => {
        setAppMode("focus");
        if (user) {
            updateLastViewedAppMode(user.uid, AppMode.Focus);
        }
        amplitudeService.logEvent(new SelectFocusViewEvent());
    };

    const handlePlanClick = () => {
        if (user && userData?.proUserInfo?.isProUser) {
            amplitudeService.logEvent(new SelectPlanningViewEvent({ hasAccess: userHasPlannerAccess }));
            if (userHasPlannerAccess) {
                setAppMode("planner");
                updateLastViewedAppMode(user.uid, AppMode.Planner);
            } else {
                BrowserUtil.openPage(ProxiedRoutes.PlannerOnboarding, !window.todesktop);
            }
        } else {
            setUpgradeCtaContext("planner", UpgradeCTASource.PLANNER);
        }
    };

    const handleExploreClick = () => {
        setAppMode("explore");
        amplitudeService.logEvent(new SelectExploreViewEvent());
    };

    const appModeContent: { [key in AppMode]: { label: string; icon: ReactNode; handleClick: () => void } } = {
        focus: { label: FOCUS_BUTTON_LABEL, icon: <StyledHeadphonesIcon />, handleClick: handleFocusClick },
        planner: { label: PLANNER_BUTTON_LABEL, icon: <StyledPlannerIcon />, handleClick: handlePlanClick },
        ...(!isMobileOnly && {
            explore: { label: EXPLORE_BUTTON_LABEL, icon: <StyledGlobeIcon />, handleClick: handleExploreClick },
        }),
    };

    const dropdownContent = (Object.keys(appModeContent).filter((key) => key !== appModeOrDefault) as AppMode[]).map(
        (key) => (
            <DropdownMenuComponents.Item key={key} onClick={() => appModeContent[key].handleClick()}>
                {appModeContent[key].icon}
                {appModeContent[key].label}
            </DropdownMenuComponents.Item>
        ),
    );

    return (
        <DropdownMenuComponents.Root open={isDropdownOpen} onOpenChange={(isOpen) => setIsDropdownOpen(isOpen)}>
            <DropdownMenuComponents.Trigger
                id={
                    "app-mode-dropdown" // required for Frigade tooltip to anchor to this component.
                }
            >
                <LifeAtButtonV2 size={"small"}>
                    {appModeContent[appModeOrDefault].icon}
                    {appModeContent[appModeOrDefault].label}
                    <CaretIconToggle showOpen={isDropdownOpen} />
                </LifeAtButtonV2>
            </DropdownMenuComponents.Trigger>
            <StyledDropdownMenuContent>{dropdownContent}</StyledDropdownMenuContent>
        </DropdownMenuComponents.Root>
    );
}
